import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  closeClubOnboarding,
  confirmClubTeams,
  getClubOnboarding,
  postponeClubOnboarding,
  resumeClubOnboarding,
  type ClubOnboarding,
  type OnboardingStep,
  type OnboardingStepKey,
} from '@volee/api-client'
import { toast } from '@volee/ui'

import { errorMessage } from '../messages/from-error.ts'
import { api } from '../services/api.ts'

/** The key the guide on the dashboard and the bar on the pages of its voci will share. */
export function clubOnboardingKey(clubId: number): readonly unknown[] {
  return ['club-onboarding', clubId]
}

/** Read off what the pages of its voci write, so it is read again each time it is drawn: back
 *  from setting the kits, the voce is done. */
export function clubOnboardingQuery(clubId: number) {
  return queryOptions<ClubOnboarding>({
    queryKey: clubOnboardingKey(clubId),
    queryFn: () => getClubOnboarding(api, clubId),
    staleTime: 0,
  })
}

type OnboardingWrite = (client: typeof api, clubId: number) => Promise<ClubOnboarding>

/** «Lo farò dopo», «Riprendi», «Chiudi» and «Sono queste»: each answers the read, which takes the cache's place.
 *  A refusal says why and reads again, since a voce may have changed on another device. */
export function useOnboardingActs(clubId: number) {
  const cache = useQueryClient()
  const act = useMutation({
    mutationFn: (write: OnboardingWrite) => write(api, clubId),
    onSuccess: (onboarding) => {
      cache.setQueryData(clubOnboardingKey(clubId), onboarding)
    },
    onError: (error) => {
      toast.error(errorMessage(error))
      void cache.invalidateQueries({ queryKey: clubOnboardingKey(clubId) })
    },
  })
  return {
    postpone: () => {
      act.mutate(postponeClubOnboarding)
    },
    resume: () => {
      act.mutate(resumeClubOnboarding)
    },
    close: () => {
      act.mutate(closeClubOnboarding)
    },
    confirmTeams: (onConfirmed: () => void) => {
      act.mutate(confirmClubTeams, { onSuccess: onConfirmed })
    },
    pending: act.isPending,
  }
}

// Every voce the API names, so an invented one in the URL is no voce at all.
const STEP_KEYS: Readonly<Record<OnboardingStepKey, true>> = {
  teams: true,
  registrations: true,
  rosters: true,
  staff: true,
  families: true,
  kits: true,
  logo: true,
}

/** The search param a voce's page is opened with from the guide, and what puts the bar on it: the
 *  same page reached from the menu has none. */
export interface OnboardingSearch {
  readonly onboarding?: OnboardingStepKey
}

export function parseOnboardingStep(value: unknown): OnboardingStepKey | undefined {
  if (typeof value !== 'string' || !Object.hasOwn(STEP_KEYS, value)) return undefined
  return value as OnboardingStepKey
}

/** Where «Avanti» leads from the page of a voce: the voce, and for one asked of every squad the
 *  squad to open it on. */
export interface OnboardingStop {
  readonly step: OnboardingStep
  readonly team: number | undefined
}

/** The squad after `team` still missing the voce; a page of no squad, or of one no longer missing
 *  it, goes to the first. Past the last squad, none: the voce is through. */
function nextSquad(missing: readonly number[], team: number | undefined): number | undefined {
  const at = team === undefined ? -1 : missing.indexOf(team)
  return at === -1 ? missing[0] : missing[at + 1]
}

/** The next squad missing the voce, then the next voce still to do in the advised order, past
 *  those done and those that wait; `null` once none is left, which leads to the dashboard. The
 *  voce that does not count is never the next one. */
export function nextOnboardingStop(
  onboarding: ClubOnboarding,
  key: OnboardingStepKey,
  team: number | undefined,
): OnboardingStop | null {
  const at = onboarding.steps.findIndex((step) => step.key === key)
  const current = onboarding.steps[at]
  if (current === undefined) return null
  const squad = nextSquad(current.teams?.missing_ids ?? [], team)
  if (squad !== undefined) return { step: current, team: squad }
  const next = onboarding.steps
    .slice(at + 1)
    .find((step) => step.state === 'todo' && !step.optional)
  return next === undefined ? null : { step: next, team: next.teams?.missing_ids[0] }
}
